import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Clock, Users, Check, Star } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { internshipPrograms } from '../data/internships';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const levels = ['All', 'Intermediate', 'Advanced'];

export default function InternshipsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('All');

  const filtered = filter === 'All'
    ? internshipPrograms
    : internshipPrograms.filter(p => p.level === filter);

  return (
    <div className="min-h-screen bg-[#060D1F]">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-20 pb-0 bg-[#060D1F] overflow-hidden">
        <div className="absolute top-10 right-20 w-80 h-80 bg-orange-500/15 rounded-full blur-[80px]" />
        <div className="absolute bottom-0 left-20 w-60 h-60 bg-blue-600/15 rounded-full blur-[60px]" />
        <div className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '36px 36px' }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-24 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.span variants={fadeUp} className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 text-orange-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
              Real Projects · Real Mentors · Real Jobs
            </motion.span>
            <motion.h1 variants={fadeUp} className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Internship <span className="text-orange-500">Programs</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-slate-400 text-lg max-w-2xl mx-auto mb-10">
              Join CALLOC's hands-on internship programs. Work alongside professionals, ship real software, and kick-start your tech career.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-10">
              {[
                { val: '38+', label: 'Students Placed' },
                { val: '4',   label: 'Programs' },
                { val: '87%', label: 'Placement Rate' },
                { val: '3',   label: 'Months Max' },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="text-3xl font-bold text-white">{s.val}</div>
                  <div className="text-slate-500 text-sm mt-1">{s.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="bg-[#060D1F] pt-12 pb-4 border-b border-white/8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {levels.map((l) => (
              <button
                key={l}
                onClick={() => setFilter(l)}
                className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  filter === l
                    ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20'
                    : 'bg-white/8 text-slate-300 hover:bg-orange-500/15 hover:text-orange-400'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAM CARDS */}
      <section className="py-12 bg-[#060D1F]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            key={filter}
            initial="hidden" animate="visible" variants={stagger}
            className="grid md:grid-cols-2 gap-8"
          >
            {filtered.map((prog) => (
              <motion.div
                key={prog.id}
                variants={fadeUp}
                whileHover={{ y: -6 }}
                className="group bg-[#0C1628] border border-white/10 rounded-3xl overflow-hidden hover:border-orange-500/40 transition-all duration-300 cursor-pointer"
                onClick={() => navigate(`/internships/${prog.id}`)}
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={prog.coverImage} alt={prog.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <span className={`${prog.iconBg} text-white text-xs font-bold px-3 py-1 rounded-lg`}>{prog.level}</span>
                    <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-lg border border-white/20">{prog.batch}</span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-2xl font-bold text-white group-hover:text-orange-300 transition-colors">{prog.title}</h3>
                    <p className="text-white/70 text-sm mt-1">{prog.shortDesc}</p>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-6 text-sm text-slate-400 mb-5">
                    <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-orange-400" /> {prog.duration}</span>
                    <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-orange-400" /> {prog.studentsCount} interns</span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-5">
                    {prog.highlights.map((h) => (
                      <div key={h} className="flex items-center gap-2 text-sm text-slate-300">
                        <div className="w-5 h-5 bg-orange-500/15 rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-orange-400" />
                        </div>
                        {h}
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {prog.skills.map((sk) => (
                      <span key={sk} className="bg-white/8 text-slate-300 text-xs px-3 py-1 rounded-full font-medium">{sk}</span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex -space-x-2">
                      {[...Array(Math.min(4, prog.studentsCount))].map((_, i) => (
                        <div key={i} className={`w-8 h-8 rounded-full border-2 border-[#0C1628] ${['bg-blue-400', 'bg-purple-400', 'bg-green-400', 'bg-rose-400'][i]}`} />
                      ))}
                      {prog.studentsCount > 4 && (
                        <div className="w-8 h-8 rounded-full border-2 border-[#0C1628] bg-white/10 flex items-center justify-center text-xs font-semibold text-slate-300">
                          +{prog.studentsCount - 4}
                        </div>
                      )}
                    </div>
                    <button className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors">
                      View Journey <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* APPLY CTA */}
      <section className="py-20 bg-[#080F1E]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-orange-400 fill-orange-400" />)}
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-4xl font-bold text-white mb-4">
              Ready to start your <span className="text-orange-500">tech journey?</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 text-lg mb-8">
              Applications open for the next batch. Spots fill fast — reach out today.
            </motion.p>
            <motion.button
              variants={fadeUp}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/')}
              className="bg-orange-500 hover:bg-orange-600 text-white px-10 py-4 rounded-xl font-semibold inline-flex items-center gap-2 transition-colors"
            >
              Apply Now <ArrowRight className="w-4 h-4" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
